import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi, userApi, subscriptionApi } from './api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};

/**
 * Returns true if the token is expired (or will expire within `bufferSec` seconds).
 */
const isTokenExpired = (token, bufferSec = 60) => {
    const decoded = parseJwt(token);
    if (!decoded || !decoded.exp) return true;
    return decoded.exp * 1000 < Date.now() + bufferSec * 1000;
};

export const AuthProvider = ({ children }) => {
    const [tokens, setTokens] = useState({ access: null, refresh: null });
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isRegisteredUser, setIsRegisteredUser] = useState(true);
    const [isListingUpload, setIsListingUpload] = useState(false);
    const [isFlatNeedUpload, setIsFlatNeedUpload] = useState(false);

    // FlatLoop Prime. Always fetched from /subscription/status — never inferred
    // from a JWT claim, which can be up to 30 minutes stale after a purchase or
    // an expiry. The server enforces the paywall regardless; this only drives UI.
    const [subscription, setSubscription] = useState(null);

    // Ref to prevent concurrent refresh calls
    const refreshingRef = useRef(false);

    // Helper: decode JWT and update all claim-based state
    const updateClaimsFromToken = useCallback((accessToken) => {
        const decoded = parseJwt(accessToken);
        if (!decoded) return;

        // Registration status
        if (decoded.isRegister === false) {
            setIsRegisteredUser(false);
        } else {
            setIsRegisteredUser(true);
        }

        // Listing / Requirement upload claims
        setIsListingUpload(!!decoded.isListingUpload);
        setIsFlatNeedUpload(!!decoded.isFlatNeedUpload);
    }, []);

    // ── Force full logout (clear everything) ──────────────────────
    const forceLogout = useCallback(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setTokens({ access: null, refresh: null });
        setUser(null);
        setIsAuthenticated(false);
        setIsListingUpload(false);
        setIsFlatNeedUpload(false);
        setSubscription(null);
    }, []);

    // ── Refresh the access token using the refresh token ──────────
    const refreshAccessToken = useCallback(async () => {
        const refreshToken = tokens.refresh || localStorage.getItem('refreshToken');
        if (!refreshToken) return false;

        // Prevent concurrent refreshes
        if (refreshingRef.current) return false;
        refreshingRef.current = true;

        try {
            const res = await authApi.refresh(refreshToken);
            if (res && res.accessToken) {
                localStorage.setItem('accessToken', res.accessToken);
                setTokens(prev => ({ ...prev, access: res.accessToken }));
                updateClaimsFromToken(res.accessToken);
                refreshingRef.current = false;
                return true;
            }
        } catch (e) {
            console.error('Failed to refresh token:', e);
            // Refresh token is also invalid → force logout
            forceLogout();
        }
        refreshingRef.current = false;
        return false;
    }, [tokens.refresh, updateClaimsFromToken, forceLogout]);

    // ── On mount: load tokens and validate ────────────────────────
    useEffect(() => {
        const loadTokens = async () => {
            const access = localStorage.getItem('accessToken');
            const refresh = localStorage.getItem('refreshToken');

            if (!access) {
                setLoading(false);
                return;
            }

            // If access token is expired, try refreshing immediately
            if (isTokenExpired(access)) {
                if (refresh) {
                    try {
                        const res = await authApi.refresh(refresh);
                        if (res && res.accessToken) {
                            localStorage.setItem('accessToken', res.accessToken);
                            setTokens({ access: res.accessToken, refresh });
                            setIsAuthenticated(true);
                            updateClaimsFromToken(res.accessToken);
                            setLoading(false);
                            return;
                        }
                    } catch (e) {
                        console.error('Startup token refresh failed:', e);
                    }
                }
                // Both tokens invalid — clear and force re-login
                forceLogout();
                setLoading(false);
                return;
            }

            // Access token is still valid
            setTokens({ access, refresh });
            setIsAuthenticated(true);
            updateClaimsFromToken(access);
            setLoading(false);
        };

        loadTokens();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Fetch user profile whenever access token changes ──────────
    useEffect(() => {
        if (!tokens.access) {
            setUser(null);
            return;
        }

        userApi.getInfo(tokens.access)
            .then(res => {
                if (res) setUser(res);
            })
            .catch(async (e) => {
                console.error("Could not load user profile:", e);
                // If the API call failed (likely 401), try refreshing
                const refreshed = await refreshAccessToken();
                if (!refreshed) {
                    // Refresh also failed → session is truly dead
                    forceLogout();
                }
            });
    }, [tokens.access]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Prime status ──────────────────────────────────────────────
    const refreshSubscription = useCallback(async () => {
        const token = tokens.access || localStorage.getItem('accessToken');
        if (!token) {
            setSubscription(null);
            return null;
        }
        try {
            const res = await subscriptionApi.getStatus(token);
            setSubscription(res);
            return res;
        } catch (e) {
            // A failed status check must never look like an active membership.
            console.error('Could not load subscription status:', e);
            setSubscription(prev => prev || null);
            return null;
        }
    }, [tokens.access]);

    useEffect(() => {
        if (tokens.access) refreshSubscription();
        else setSubscription(null);
    }, [tokens.access, refreshSubscription]);

    // ── Proactive token refresh timer ─────────────────────────────
    // Refreshes the token 60 seconds before it expires
    useEffect(() => {
        if (!tokens.access) return;

        const decoded = parseJwt(tokens.access);
        if (!decoded || !decoded.exp) return;

        const expiresInMs = decoded.exp * 1000 - Date.now();
        // Refresh 60 seconds before expiry (minimum 10 seconds from now)
        const refreshInMs = Math.max(expiresInMs - 60000, 10000);

        const timerId = setTimeout(() => {
            refreshAccessToken();
        }, refreshInMs);

        return () => clearTimeout(timerId);
    }, [tokens.access, refreshAccessToken]);

    // ── Refresh on tab re-focus (user returns after being away) ───
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && tokens.access) {
                if (isTokenExpired(tokens.access)) {
                    refreshAccessToken();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [tokens.access, refreshAccessToken]);

    const login = async (countryCode, phoneNumber) => {
        return await authApi.register(countryCode, phoneNumber);
    };

    const verifyOtp = async (countryCode, phoneNumber, otp) => {
        const res = await authApi.verifyOtp(countryCode, phoneNumber, otp);
        const { accessToken, refreshToken, isRegister } = res;

        localStorage.setItem('accessToken', accessToken);
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

        setTokens({ access: accessToken, refresh: refreshToken });
        setIsAuthenticated(true);
        updateClaimsFromToken(accessToken);

        return { isNewUser: !isRegister };
    };

    const logout = async () => {
        if (tokens.refresh) {
            try {
                await authApi.logout(tokens.refresh, tokens.access);
            } catch (e) {
                console.error('Logout error', e);
            }
        }
        forceLogout();
    };

    return (
        <AuthContext.Provider value={{
            tokens,
            user,
            loading,
            isAuthenticated,
            isRegisteredUser,
            setIsRegisteredUser,
            isListingUpload,
            isFlatNeedUpload,
            subscription,
            isSubscribed: !!subscription?.subscribed,
            refreshSubscription,
            login,
            verifyOtp,
            logout,
            refreshAccessToken
        }}>
            {children}
        </AuthContext.Provider>
    );
};
